import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { SearchBox } from 'react-instantsearch';
import { get, debounce } from 'lodash';
import avo from 'analytics/avo';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';

import isClient from 'commons/isClient';
import useIsBrowser from 'hooks/useIsBrowser';
import isNewConsumerApp from 'commons/isNewConsumerApp';

import IconSearch from 'components/icons/IconSearch';
import IconClose from 'components/icons/iconClose';
import useScreen from 'hooks/useScreen';
import useUserCurrentPageType from 'hooks/useUserCurrentPageType';
import { getAdvancedSearchUrlForQueryAndIndex } from 'components/consumer/consumer-search/commons/helpers';

const JBSearchBox = ({
  advanced,
  autoFocus,
  defaultRefinement,
  forPage,
  index,
  inMobileNav = false,
  isActive,
  placeholder = 'What can we help you find?',
  setInactive,
  variant,
  initialQueryValue,
  setSearchStateQuery,
  toggleMobileSearchBar,
  handleSearchBarFocus,
  ...otherProps
}) => {
  const history = useHistory();
  const location = useLocation();
  const currentPage = useUserCurrentPageType();
  const [searchQuery, updateSearchQuery] = useState(initialQueryValue);
  const { isMobile } = useScreen();
  const isBrowser = useIsBrowser();

  const debouncedSearchStateQueryUpdate = useMemo(
    () =>
      debounce(updatedValue => {
        if (
          ((!isNewConsumerApp && !advanced) || isBrowser) &&
          setSearchStateQuery
        ) {
          setSearchStateQuery(updatedValue);
        }
      }, 250),
    [advanced, isBrowser, setSearchStateQuery]
  );

  const handleQueryChange = useCallback(
    event => {
      updateSearchQuery(event.target.value);
      debouncedSearchStateQueryUpdate(event.target.value);
    },
    [updateSearchQuery, debouncedSearchStateQueryUpdate]
  );

  const goToAdvancedSearch = () => {
    if (isBrowser) {
      avo.productsSearched({
        query: searchQuery,
        location: currentPage,
        searchTarget: avo.SearchTarget.FULL_RESULTS,
      });
      const newUrl = getAdvancedSearchUrlForQueryAndIndex(
        searchQuery,
        index || ALGOLIA_INDEX_NAME.PRODUCTS,
        '*'
      );
      history.push(newUrl, {
        instantQuery: searchQuery,
      });
      otherProps?.toggleSearchOpen();
    }
  };

  // instant-search-form__form_inner
  return (
    <div
      className={classNames(
        '[&_i]:w-[18px] [&_i]:h-[18px] [&_i]:text-[18px] [&_i]:block [&_i]:absolute [&_i]:top-1/2 [&_i]:mt-[-9px] [&_i]:right-10 [&_i]:cursor-pointer [&_i]:[border-right:1px_solid_#dfe0e1] bg-gray-light8',
        {
          'py-3': variant === 'ConsumerInstantSearch',
        }
      )}
    >
      <div
        className={classNames(
          'mx-5 my-0 flex flex-row items-stretch relative box-border min-[1440px]:mx-[3.333vw] min-[1440px]:my-0',
          {
            'px-0 py-[5px]': variant !== 'ConsumerInstantSearch',
            '!ml-3': inMobileNav,
            'px-3 bg-white rounded-[5px] border border-gray-light6 [&_.ais-SearchBox-input]:lg:!pl-0 [&_.ais-SearchBox-input]:lg:!text-left [&_.ais-SearchBox-input]:lg:!indent-[12px] [&_.ais-SearchBox-input::placeholder]:lg:!text-left [&_.ais-SearchBox-input.hasValue]:lg:!indent-[24px]':
              variant === 'ConsumerInstantSearch',
            '[&_button.ais-SearchBox-reset]:!block':
              variant === 'ConsumerInstantSearch' && isActive,
            '[&_.ais-SearchBox-input]:indent-[12px]':
              variant === 'InstantSearchMobile',
          }
        )}
      >
        <IconSearch
          className={classNames('self-center min-w-[16px]', {
            hidden: variant === 'InstantSearchMobile',
          })}
          width={18}
          height={18}
        />{' '}
        {advanced && (
          <div className="inline-flex w-full pl-2">
            <div className="flex w-full">
              <input
                className="text-left pl-[30px] [appearance:unset] bg-none [background:none] border-[1px] border-solid border-transparent text-[#333] block [font-size:1rem] font-bold h-10 leading-[1.45rem] tracking-[-.0125rem] w-full"
                type="search"
                placeholder={placeholder}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                maxLength={512}
                value={searchQuery}
                onChange={handleQueryChange}
              />
            </div>
          </div>
        )}
        {!advanced && (
          <div
            className="inline-flex w-full"
            onClick={handleSearchBarFocus}
            role="button"
            tabIndex={0}
          >
            {/* 
              Deprecated & archived documentation here
              https://www.algolia.com/doc/deprecated/instantsearch/react/v6/api-reference/search-box/ 
            */}
            <SearchBox
              translations={{}}
              /* TODO: CON-7887: (Codemod generated): Move this into `InstantSearch`'s `initialUiState` prop.
              See https://www.algolia.com/doc/guides/building-search-ui/upgrade-guides/react/#default-refinements */
              defaultRefinement={defaultRefinement}
              autoFocus={autoFocus}
              onFocus={event => {
                if (!(advanced || forPage === 'associate')) {
                  event.currentTarget.placeholder = placeholder;
                }
              }}
              onBlur={event => {
                if (!isMobile) {
                  return;
                }

                // Capture event values immediately before they become undefined
                const inputElement = event.currentTarget;
                const inputValue = inputElement?.value;

                if (inputElement) {
                  inputElement.placeholder = placeholder;
                }

                if (inputValue?.length === 0) {
                  const requiredNode = get(
                    inputElement,
                    'parentNode.parentNode.parentNode.parentNode'
                  );
                  if (requiredNode) requiredNode.classList.remove('active');
                }

                if (setInactive !== undefined) {
                  setInactive(true);
                }
              }}
              onInput={event => {
                if (event.currentTarget.value?.length > 0) {
                  event.currentTarget?.classList.add('hasValue');
                } else {
                  event.currentTarget?.classList.remove('hasValue');
                }
              }}
              onChange={handleQueryChange}
              onKeyDown={e => {
                if (isMobile) {
                  updateSearchQuery(e.currentTarget.value);
                  if (otherProps.setSearchEventParams) {
                    otherProps.setSearchEventParams({
                      searchQuery: e.currentTarget.value,
                      currentPage,
                    });
                  }
                  return;
                }
                if (
                  forPage !== 'associate' &&
                  +e.keyCode === 13 &&
                  e.target.value !== '' &&
                  isClient()
                ) {
                  avo.productsSearched({
                    query: e.target.value,
                    location: currentPage,
                    searchTarget: avo.SearchTarget.FULL_RESULTS,
                  });
                  const newUrl = getAdvancedSearchUrlForQueryAndIndex(
                    e.target.value,
                    index || 'products_jb4',
                    '*'
                  );
                  if (location.pathname === '/search/') {
                    window.location.href = newUrl;
                  } else {
                    history.push(newUrl, {
                      instantQuery: e.target.value,
                    });
                    otherProps.toggleSearchOpen();
                  }
                }
              }}
              onReset={async event => {
                const target = event.currentTarget;

                // Remove hasValue class from input
                const form = target?.closest('.ais-SearchBox-form');
                form
                  ?.querySelector('.ais-SearchBox-input')
                  ?.classList.remove('hasValue');

                if (!(advanced || forPage === 'associate')) {
                  await otherProps.toggleSearchOpen();
                  if (isMobile) {
                    setInactive?.();
                    const requiredNode = get(
                      target,
                      'parentNode.parentNode.parentNode'
                    );
                    if (requiredNode) {
                      requiredNode.classList.remove('active');
                    }
                    if (searchQuery && searchQuery.length > 0) {
                      avo.productsSearched({
                        query: searchQuery,
                        location: currentPage,
                        searchTarget: avo.SearchTarget.EXIT_SEARCH,
                      });
                    }
                  }
                }
              }}
              placeholder={placeholder}
            />{' '}
          </div>
        )}{' '}
        {isBrowser && variant === 'InstantSearchMobile' && (
          <div className="flex gap-2 items-center">
            <IconSearch
              className="self-center min-w-[16px] cursor-pointer"
              width={18}
              height={18}
              onClick={goToAdvancedSearch}
            />

            <div className="text-gray">|</div>

            <IconClose
              className="self-center min-w-[16px] cursor-pointer"
              width={14}
              height={14}
              color="#424242"
              onClick={toggleMobileSearchBar}
            />
          </div>
        )}
      </div>{' '}
      {isMobile && (
        <div
          className={classNames(
            'h-0 w-0 top-[65px] z-[-1] left-0 fixed bg-white [transition:opacity_.3s_ease-out] opacity-0 lg:absolute lg:bg-transparent',
            {
              active: isActive,
              hidden: variant === 'InstantSearchMobile',
              'lg:opacity-100 lg:h-screen lg:w-screen lg:top-[-100px] lg:absolute lg:[transition:opacity_.3s_ease-out]': isActive,
            }
          )}
        />
      )}
    </div>
  );
};

export default JBSearchBox;
